<template>
    <div class=" pb-2" style="padding-top: 2px;">
        <f-currency-field
            v-model         = "amount.net"
            :rules          = "[netAmountRule]"
            class           = "money"
            :label          = "label"
            :error-messages = "netamountError"
        />

        <v-switch
            v-if            = "hasCalculation && !showCosts"
            v-model         = "item.acceptDifference"
            class           = "mt-2 pt-0"
            label           = "Differenz akzeptiert"
            :ripple         = "false"
            @change         = "validate()"
            hide-details
            :style="showCosts ? '' : 'margin-bottom: 26px;'"
        />

        <div
            v-if            = "spacer"
            class           = "mt-9"
        />

        <f-currency-field
            v-if="showCosts"
            class           = "money"
            :label          = "$t('invoice.cost.material')"
            v-model         = "amount.costMaterial"
            :rules          = "[costsEqualNetAmountRule]"
            :error-messages = "costMaterialError"
            :hide-details   = "false"
            ref="material"
        />

        <f-currency-field
            v-if="showCosts"
            v-model         = "amount.costWage"
            class           = "money pb-2"
            :label          = "$t('invoice.cost.wage')"
            :rules          = "[costsEqualNetAmountRule]"
            :error-messages = "costWageError"
            :hide-details   = "true"
            ref="wage"
        />
    </div>
</template>

<script>

import FCurrencyField from "@/modules/core/components/utils/FCurrencyField.vue";

export default {
    name: "PIncInvoiceAmount",

    components: {
        FCurrencyField
    },

    props: {
        label: {
            required: false,
            default : function () {
                return this.$t('invoice.cost.net');
            }
        },

        status: {
            required: true,
            type    : Object,
        },

        item: {
            required: false,
            type    : Object,
            default : function () {
                return {

                };
            }
        },

        amount: {
            required: false,
            type    : Object,
            default : function () {
                return {

                };
            }
        },

        calculation: {
            required: false,
            type    : Function,
            default : function () {
                return null;
            }
        },

        validateAmount: {
            required: false,
            type    : Boolean,
            default : function () {
                return true;
            }
        },

        showCosts: {
            required: false,
            type    : Boolean,
            default : true
        },

        acceptSpacer: {
            required: false,
            type    : Boolean,
            default : function () {
                return false
            }
        },

        form: {

        },
    },

    data: () => ({
        netamountError   : [],
        costMaterialError: [],
        costWageError    : [],
    }),

    watch: {
        net     : 'validate',
        material: 'validate',
        wage    : 'validate',
    },

    computed: {
        net () {
            return this.item.net;
        },

        hasCalculation () {
            return this.calculation && this.calculation () !== null;
        },

        spacer () {
            return !this.hasCalculation && this.acceptSpacer === true;
        }
    },

    methods: {
        /**
         * Validates the net amount input.
         */
        netAmountRule (value) {
            if (this.validateAmount) {
                if ((!value || value.length === 0) && this.status.type === 'T') {
                    this.netamountError    = [this.$t("rules.required")];
                    this.costMaterialError = [];
                    this.costWageError     = [];
                    return false;
                }
            } else {
                return true;
            }

            if (!this.netAmountEqualsBidCalculationRule ()) {
                return false;
            }

            return this.costsEqualNetAmountRule();
        },

        /**
         * Validates the net amount input against the bid calculation.
         */
        netAmountEqualsBidCalculationRule ()
        {
            /*
             + If validation is not required or the difference is always accepted by the user, clear all errors
             * and stop validation.
             */
            if (!this.calculation() || this.showCosts) {
                return this.clearError();
            }

            /*
             * If the netamount is empty or zero, clear all errors and stop validation.
             */
            const netamount = this.amount.net ? this.amount.net : 0;
            if (netamount === 0) {
                return this.clearError();
            }

            /*
             * If the calculation fields are empty or equal zero as well, clear all errors and stop validation.
             */
            const price = this.calculation ();
            if (netamount === 0 && price === 0) {
                return this.clearError();
            }

            if (netamount !== price && !this.item.acceptDifference && this.validateAmount) {
                this.netamountError    = ["Angebotspreis ungleich Rechnung"];
                this.costMaterialError = [];
                this.costWageError     = [];

                return false;
            }

            return this.clearError();
        },

        /**
         * Validates the net amount input against the sum of costs.
         */
        costsEqualNetAmountRule ()  {
            if (!this.showCosts) {
                return true;
            }

            const netamount = this.amount.net          ? this.round(this.amount.net)      : 0;
            const material  = this.amount.costMaterial ? this.round(this.amount.costMaterial) : 0;
            const wage      = this.amount.costWage     ? this.round(this.amount.costWage)     : 0;

            if (netamount === 0  && (material || wage)) {
                return this.setError("cost-without-netamount");
            }

            if (netamount === 0) {
                return this.clearError();
            }

            const sum = this.round(material + wage);
            if (sum !== 0 && sum !== netamount) {
                return this.setError("cost-netamount-difference");
            } else {
                this.netamountError = [];
                this.costMaterialError = [];
                this.costWageError     = [];
            }

            if (this.netamountError.length > 0) {
                this.costMaterialError = [];
                this.costWageError     = [];
                return true;
            }

            return this.clearError();
        },

        setError (message) {
            this.netamountError    = [this.$t("invoice.error." + message)];
            this.costMaterialError = [""];
            this.costWageError     = [""];

            return false;
        },

        clearError () {
            this.netamountError    = [];
            this.costMaterialError = [];
            this.costWageError     = [];

            return true;
        },

        validate () {
            this.form.validate();
        },

        round (value) {
            return (Math.round(value * 100)/100);
        }
    }
}
</script>

<style scoped>

.money ::v-deep input{
    text-align: right;
}

</style>